import {
  countryName,
  countryOptions,
  Pick,
  formatRange,
  formatWhole,
  type DocsScreen,
  type Plan,
} from '@trippath/shared';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';

import { day, dayShort, percent } from '../../lib/format';

/** The trip against its budget (the App mockup's dark card): lowest, expected, highest. */
export function BudgetCard({ plan }: { plan: Plan }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  // The whole trip's money, or each one's part of it: the toggle where more than one share it.
  const people = plan.trip.people;
  const [each, setEach] = useState(false);
  const part = <T extends { minor: number }>(money: T): T =>
    each ? { ...money, minor: Math.round(money.minor / people) } : money;
  const totals = {
    ...plan.totals,
    total: { min: part(plan.totals.total.min), max: part(plan.totals.total.max) },
    expected: part(plan.totals.expected),
    paid: part(plan.totals.paid),
    toPay: part(plan.totals.toPay),
    estimate: { min: part(plan.totals.estimate.min), max: part(plan.totals.estimate.max) },
    over: plan.totals.over && part(plan.totals.over),
  };
  const budget = part(plan.trip.budget);
  const scale = Math.max(totals.total.max.minor, budget.minor, 1);
  const at = (minor: number) => `${String((minor / scale) * 100)}%`;
  const cash = { ...totals.expected, minor: totals.expected.minor - totals.paid.minor };

  return (
    <section className="budget-card" aria-labelledby="budget-title">
      <div className="budget-head">
        <h2 id="budget-title">{t('plan.budget')}</h2>
        {people > 1 && (
          <span className="segmented" role="group" aria-label={t('plan.reckoned')}>
            <button
              type="button"
              aria-pressed={!each}
              onClick={() => {
                setEach(false);
              }}
            >
              {t('plan.wholeTrip')}
            </button>
            <button
              type="button"
              aria-pressed={each}
              onClick={() => {
                setEach(true);
              }}
            >
              {t('plan.perPerson', { count: people })}
            </button>
          </span>
        )}
      </div>
      <dl className="budget-figures">
        <div>
          <dt>{t('plan.min')}</dt>
          <dd>{formatWhole(totals.total.min, lang)}</dd>
        </div>
        <div className="budget-expected">
          <dt>{t('plan.expected')}</dt>
          <dd>{formatWhole(totals.expected, lang)}</dd>
        </div>
        <div>
          <dt>{t('plan.max')}</dt>
          <dd className={totals.over === null ? undefined : 'warn'}>
            {formatWhole(totals.total.max, lang)}
          </dd>
        </div>
      </dl>
      <div className="budget-bar" aria-hidden="true">
        <span className="budget-bar-min" style={{ width: at(totals.total.min.minor) }} />
        <span
          className="budget-bar-expected"
          style={{
            left: at(totals.total.min.minor),
            width: at(totals.expected.minor - totals.total.min.minor),
          }}
        />
        <span
          className="budget-bar-max"
          style={{
            left: at(totals.expected.minor),
            width: at(totals.total.max.minor - totals.expected.minor),
          }}
        />
        <span className="budget-bar-budget" style={{ left: at(budget.minor) }} />
      </div>
      <dl className="budget-states">
        <div>
          <dt>{t('plan.paid')}</dt>
          <dd className="paid">{formatWhole(totals.paid, lang)}</dd>
        </div>
        <div>
          <dt>{t('plan.toPay')}</dt>
          <dd className="to-pay">{formatWhole(totals.toPay, lang)}</dd>
        </div>
        <div>
          <dt>{t('plan.estimated')}</dt>
          <dd>{formatRange(totals.estimate, lang, true)}</dd>
        </div>
      </dl>
      <p className="budget-foot">
        <span>{t('plan.cashNeeded', { amount: formatWhole(cash, lang) })}</span>
        <span>{t('plan.budgetIs', { amount: formatWhole(budget, lang) })}</span>
      </p>
      {/* What cancellations cost, and what of them is still to come back. */}
      {(totals.lost.minor > 0 || totals.refundsPending.minor > 0) && (
        <p className="budget-foot budget-lost">
          {totals.lost.minor > 0 && (
            <span>{t('plan.lost', { amount: formatWhole(totals.lost, lang) })}</span>
          )}
          {totals.refundsPending.minor > 0 && (
            <span>
              {t('plan.refundsPending', { amount: formatWhole(totals.refundsPending, lang) })}
            </span>
          )}
        </p>
      )}
      {(totals.over !== null || plan.biggestShare !== null) && (
        <p className="budget-warn" role="status">
          {totals.over !== null && t('plan.over', { amount: formatWhole(totals.over, lang) })}{' '}
          {plan.biggestShare !== null &&
            t('plan.share', {
              country: countryName(plan.biggestShare.country, lang),
              percent: percent(plan.biggestShare.basisPoints, lang),
            })}
        </p>
      )}
    </section>
  );
}

/** The trip in four numbers: its days, a day's cost, its countries and cities, what is booked. */
export function StatsCard({ plan }: { plan: Plan }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const { totals } = plan;
  const average = {
    ...totals.expected,
    minor: totals.days > 0 ? Math.round(totals.expected.minor / totals.days) : 0,
  };
  const booked = { ...totals.paid, minor: totals.paid.minor + totals.toPay.minor };
  const cities = plan.stops.filter((stop) => !stop.unresolved).length;

  return (
    <dl className="panel stats-card" aria-label={t('plan.stats')}>
      <div>
        <dt>
          {t('plan.statDays', {
            from: dayShort(plan.trip.start, lang),
            to: dayShort(plan.trip.end, lang),
          })}
        </dt>
        <dd>{totals.days}</dd>
      </div>
      <div>
        <dt>{t('plan.statAverage')}</dt>
        <dd>{formatWhole(average, lang)}</dd>
      </div>
      <div>
        <dt>{t('plan.statPlaces')}</dt>
        <dd>
          {plan.countries.length} · {cities}
        </dd>
      </div>
      <div>
        <dt>{t('plan.statBooked', { paid: formatWhole(totals.paid, lang) })}</dt>
        <dd>{formatWhole(booked, lang)}</dd>
      </div>
    </dl>
  );
}

/** How long each country of the route is stayed in against the most the passport allows. */
export function VisaCard({
  docs,
  onPassport,
}: {
  docs: DocsScreen;
  /** The passport chosen here, the card read again with it. */
  onPassport: (passport: string | null) => void;
}) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const limited = docs.crossings.flatMap((crossing) =>
    crossing.rule && crossing.rule.days > 0 ? [{ crossing, max: crossing.rule.days }] : [],
  );

  return (
    <section className="panel visa-card" aria-labelledby="visa-title">
      <h2 id="visa-title">{t('plan.visaTitle')}</h2>
      <Pick
        label={t('plan.visaPassport')}
        options={countryOptions(lang)}
        value={docs.passport}
        searchable
        onChange={onPassport}
        onClear={() => {
          onPassport(null);
        }}
      />
      {docs.passport !== null && limited.length === 0 && (
        <p className="quiet">{t('plan.visaNone')}</p>
      )}
      {limited.length > 0 && (
        <ul>
          {limited.map(({ crossing, max }) => (
            <li key={`${crossing.country}-${String(crossing.entry)}`}>
              <span className="visa-line">
                <span>{countryName(crossing.country, lang)}</span>
                <span className="mono">
                  {crossing.days} / {max}
                </span>
              </span>
              <span className="visa-bar" aria-hidden="true">
                <span
                  className={crossing.days > max ? 'over' : undefined}
                  style={{
                    width: `${String(Math.min(100, Math.round((crossing.days / max) * 100)))}%`,
                  }}
                />
              </span>
            </li>
          ))}
        </ul>
      )}
      <p className="visa-note">{t('plan.visaNote')}</p>
    </section>
  );
}

/** The documents in short: the passport, the cover, and the files of the trip. */
export function DocsCard({ trip, docs }: { trip: string; docs: DocsScreen }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const passport = docs.documents.find((d) => d.kind === 'passport' && d.mine);
  const insurance = docs.documents.find((d) => d.kind === 'insurance');
  const files = docs.documents.filter((d) => d.file !== null).slice(0, 4);
  const stopOf = (id: string | null) => docs.stops.find((s) => s.id === id)?.city;

  return (
    <section className="panel docs-card" aria-labelledby="docs-card-title">
      <h2 id="docs-card-title">
        <Link to={`/trips/${trip}/docs`}>{t('docs.documents')}</Link>
      </h2>
      <p>
        <span>{t('docs.kinds.passport')}</span>
        {passport?.validTo ? (
          <span className="docs-ok">
            {t('plan.validTo', { day: day(passport.validTo, lang) })}
          </span>
        ) : (
          <span className="docs-missing-text">{t('docs.notAdded')}</span>
        )}
      </p>
      <p>
        <span>{t('docs.kinds.insurance')}</span>
        {insurance ? (
          <span className="docs-ok">{insurance.name}</span>
        ) : (
          <span className="docs-missing-text">{t('docs.notAdded')}</span>
        )}
      </p>
      {files.map((file) => (
        <p key={file.id}>
          <span>{file.name}</span>
          <span className="quiet">{stopOf(file.stop) ?? t(`docs.kinds.${file.kind}`)}</span>
        </p>
      ))}
    </section>
  );
}
